import { useCallback, useMemo } from 'react';

import { ForwardMethod, MessageActionType, TargetStatus } from '../../../modules/Apps';
import { useCreateTarget } from '../../../modules/Apps/Targets';
import { PopoverStateReturn } from '../../common/Drawer/Drawer.context';
import { FormDrawer } from '../../common/FormDrawer';
import { useToasterContext } from '../../common/Toaster';

import { createTargetsFormValidationSchema, TargetsForm } from './TargetsForm';
import { TargetsFormValues, TargetsFormValuesToDto } from './types';

interface CreateTargetsDrawerProps {
  state: PopoverStateReturn;
  appId: string;
}

export function CreateTargetsDrawer({ state, appId }: CreateTargetsDrawerProps) {
  const { mutateAsync } = useCreateTarget();
  const { showSuccess, showError } = useToasterContext();

  const handleSubmit = useCallback(
    async (values: TargetsFormValues) => {
      await mutateAsync(TargetsFormValuesToDto(values), {
        onSuccess: () => showSuccess('Target successfully created.'),
        onError: () =>
          showError('There was an error creating the target. Please, try again later.'),
      });
    },
    [mutateAsync, showError, showSuccess],
  );

  const initialValues: TargetsFormValues = useMemo(
    () => ({
      appId,
      authHeaders: [],
      status: TargetStatus.Enabled,
      forwardMethod: ForwardMethod.Get,
      forwardUrl: '',
      name: '',
      scopes: [],
      message: '',
      messageActionType: MessageActionType.None,
    }),
    [appId],
  );

  const validationSchema = useMemo(() => createTargetsFormValidationSchema(), []);

  return (
    <FormDrawer
      state={state}
      handleSubmit={handleSubmit}
      initialValues={initialValues}
      title="Add App Target"
      subtitle="Create a new target to forward inbound SMS messages to another application"
      validationSchema={validationSchema}
      validateOnBlur={false}
    >
      {() => <TargetsForm appId={appId} />}
    </FormDrawer>
  );
}
